import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Palette, Users, FileText, Activity } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get(`/tenants/${id}`)
      .then((r) => { setTenant(r.data); setForm(r.data); })
      .catch(() => toast.error('Failed to load tenant'));
  }, [id]);

  const handleSave = async () => {
    try {
      const res = await api.put(`/tenants/${id}`, {
        name: form.name, domain: form.domain,
        plan_type: form.plan_type, is_active: form.is_active,
      });
      setTenant(res.data);
      setEditing(false);
      toast.success('Tenant updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  if (!tenant) return <div className="text-[#A3A3A3] font-ibm">Loading tenant...</div>;

  const statItems = [
    { label: 'Kullanıcılar', value: tenant.stats?.users_count || 0, icon: Users },
    { label: 'İçerik', value: tenant.stats?.content_count || 0, icon: FileText },
    { label: 'İşler', value: tenant.stats?.jobs_count || 0, icon: Activity },
  ];

  return (
    <div data-testid="tenant-detail-page" className="animate-fade-in space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/tenants')} className="text-[#A3A3A3] hover:text-white" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">{tenant.name}</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">{tenant.domain || 'Alan adı belirlenmedi'}</p>
        </div>
        <Link to={`/tenants/${id}/brand`}>
          <Button variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5" data-testid="edit-brand-btn">
            <Palette size={16} className="mr-2" /> Marka Profili
          </Button>
        </Link>
        <Button onClick={() => setEditing(!editing)} data-testid="edit-tenant-btn" className={editing ? 'bg-white text-black hover:bg-[#E5E5E5]' : 'bg-white/10 text-white hover:bg-white/20'}>
          {editing ? 'İptal' : 'Düzenle'}
        </Button>
      </div>

      <Separator className="bg-[#262626]" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statItems.map((s) => (
          <Card key={s.label} className="stat-card">
            <CardContent className="p-5 flex items-center gap-4">
              <s.icon size={20} className="text-[#FACC15]" />
              <div>
                <p className="text-2xl font-outfit font-bold text-white">{s.value}</p>
                <p className="text-xs text-[#737373] font-ibm">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
        <Card className="stat-card">
          <CardContent className="p-5">
            <Badge variant="outline" className={tenant.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
              {tenant.is_active ? 'Aktif' : 'Devre Dışı'}
            </Badge>
            <p className="text-xs text-[#737373] font-ibm mt-2 capitalize">Plan: {tenant.plan_type}</p>
          </CardContent>
        </Card>
      </div>

      {editing && (
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader>
            <CardTitle className="font-outfit text-lg text-white">Bayi Düzenle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Name</Label>
                <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="edit-name-input" />
              </div>
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Domain</Label>
                <Input value={form.domain || ''} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="edit-domain-input" />
              </div>
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Plan</Label>
                <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="edit-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A3A3A3] font-ibm text-sm">Status</Label>
                <Select value={form.is_active ? 'active' : 'disabled'} onValueChange={(v) => setForm({ ...form, is_active: v === 'active' })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditing(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white">İptal</Button>
              <Button onClick={handleSave} data-testid="save-tenant-btn" className="bg-white text-black hover:bg-[#E5E5E5]">Değişiklikleri Kaydet</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#141414] border-[#262626]">
        <CardHeader>
          <CardTitle className="font-outfit text-lg text-white">Kullanım Limitleri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(tenant.usage_limits || {}).map(([key, value]) => (
              <div key={key} className="p-3 bg-[#0A0A0A] rounded-md border border-[#1A1A1A]">
                <p className="text-xs text-[#737373] font-ibm capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-xl font-outfit font-bold text-white mt-1">{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
